import { useState, useEffect, useRef } from 'react'
import { Grid, canGrid, gridColumnsNeeded } from './Grid.jsx'
import { useGridCapacity } from './gridCapacity.js'
import { ItemStack } from './ItemStack.jsx'
import { isRequired, isAnswered, isRenderable } from './kinds.js'


/**
 * Picks the layout for one instrument.
 *
 * griddable comes off the questionnaire in config, never off a hunch about the
 * instrument, and canGrid() checks the items actually share one scale before
 * trusting it. AUDIT, EPDS, ISI and PCL-5 fall to the stack on both counts.
 *
 * EMA beeps do not come through here at all: they carry their own prompt and
 * their own scale per item, so the beep screen uses ItemStack directly.
 */
export function QuestionnaireBody({
  questionnaire,
  items,
  formats,
  answers,
  onAnswer,
  onComplete,
  disabled,
  // timeframeInTitle rides ...stackProps into ItemStack untouched, like every
  // other stack option - naming it here was a syntax error (rest must be last)
  // and redundant plumbing in one move.
  ...stackProps
}) {
  // The grid branch reports its action to the band, exactly as ItemStack does.
  const { onAction } = stackProps
  /*
   * ═══════════════════════════════════════════════════════════════════════
   * EVERY HOOK IN THIS COMPONENT IS ABOVE THE EARLY RETURN. NOTHING MAY GO
   * BELOW IT. This is a hard rule here, not a preference.
   *
   * I wrote that as a comment once and only half did it. Two hooks stayed
   * below `if (!gridded)`, so the stack branch called five hooks and the grid
   * branch called seven — and the comment above them claimed the violation was
   * fixed, which is how it survived review, including mine.
   *
   * It could not crash while the branch depended only on `questionnaire.id`,
   * because Day 0 keys this component on that id and a change of branch was
   * always a remount. AD-8 removed that protection: capacity is live now, so
   * the branch flips WITHIN one mount when she changes her text size. At the
   * threshold where the grid gives up its columns, React rendered the same
   * instance with fewer hooks than last time and threw #300 — the battery
   * went to the error boundary at exactly the setting chosen by the
   * participant who most needs larger text.
   *
   * So the mechanism that makes capacity live is the mechanism that crashed,
   * and the fix is not to make capacity less live. It is that a component
   * whose branch can change at runtime may not have a hook behind that branch.
   * ═══════════════════════════════════════════════════════════════════════
   *
   * §3.8: the incomplete state is not entered until the participant asks to
   * leave. Nothing is flagged on arrival.
   */
  const [asked, setAsked] = useState(false)
  const gridActionRef = useRef(onAction)
  gridActionRef.current = onAction

  /*
   * AD-8 — THE GRID IS ALLOWED ONLY IF ITS COLUMNS FIT AT THE CURRENT TEXT
   * SIZE, not at a text size someone assumed.
   *
   * Three independent questions, and conflating any two of them is what broke:
   *   griddable    config's declaration
   *   canGrid      do the items actually share one scale (data)
   *   capacity     do that many columns fit RIGHT NOW (viewport + root font)
   *
   * When capacity fails, the section renders as §4.1 stacked rows. That is not
   * a degraded mode: it is the same form a 7-option section already uses, it
   * cannot wrap, and a column that cannot wrap cannot mis-label an answer.
   */
  const capacity = useGridCapacity()
  const columnsNeeded = gridColumnsNeeded(items, formats)
  const gridded =
    questionnaire?.griddable === true &&
    canGrid(items, formats) &&
    columnsNeeded > 0 &&
    columnsNeeded <= capacity

  // The server's list, not a second opinion about it. A grid answers many items
  // at once, so this is the whole section in one screen and the outstanding
  // list IS the gate.
  const outstandingIds =
    stackProps.sectionOutstanding ??
    items
      .filter(
        (item) =>
          (stackProps.requiredIds ? stackProps.requiredIds.has(item.item_id) : isRequired(item)) &&
          isRenderable(formats[item.response_format]) &&
          !isAnswered(answers?.[item.item_id]),
      )
      .map((item) => item.item_id)

  const outstandingItems = outstandingIds
    .map((id) => items.find((item) => item.item_id === id))
    .filter(Boolean)
  const blocked = outstandingItems.length > 0

  /*
   * The grid reports its action upward so the band draws it — the same
   * contract ItemStack uses. Pressing while blocked enters the incomplete
   * state (which is what reveals the note) and never advances.
   */
  // Stable callback reading the latest state, for the same reason as ItemStack:
  // the band keeps this across renders and must not press a stale closure.
  const gridPressRef = useRef(null)
  gridPressRef.current = () => {
    if (!blocked) return onComplete?.()
    setAsked(true)
    // Same focus move as the stack: the note alone is only visible help.
    const first = outstandingItems[0]
    if (first) {
      requestAnimationFrame(() => {
        const row = document.querySelector(`[data-item-id="${first.item_id}"]`)
        row?.scrollIntoView({ block: 'center' })
        row?.querySelector('input,button,select,textarea')?.focus?.()
      })
    }
  }
  useEffect(() => {
    /*
     * The HOOK is unconditional; its BODY is not. On the stack branch ItemStack
     * owns the action and reports it through the same `onAction`, so this must
     * stay silent or the two would fight over the band's primary button.
     *
     * Ordering is safe in both directions: React flushes every cleanup in a
     * commit before it runs any create, so ItemStack unmounting and this effect
     * claiming the action cannot interleave.
     */
    if (!gridded) return
    gridActionRef.current?.({
      label: 'Continue',
      press: () => gridPressRef.current?.(),
      incomplete: blocked,
      canGoBack: false,
      goBack: null,
      /*
       * NO NOTICE - operator ruling, same as the stack. The flagged rows carry
       * the red mark and the press scrolls to the first one; a count in a bar
       * adds nothing she cannot already see.
       */
      notice: null,
    })
    // `gridded` is in the deps because the branch can now flip inside one
    // mount: without it, growing the text back down would leave the band
    // holding the stack's action while the grid is on screen.
     
  }, [gridded, blocked, disabled, items.length, asked])

  // THE ONLY BRANCH, AND IT IS BELOW EVERY HOOK. Keep it that way.
  if (!gridded) {
    return (
      <ItemStack
        items={items}
        formats={formats}
        answers={answers}
        onAnswer={onAnswer}
        onComplete={onComplete}
        disabled={disabled}
        instruction={questionnaire?.instruction}
        timeframe={questionnaire?.timeframe}
        {...stackProps}
      />
    )
  }

  return (
    <div>
      <Grid
        questionnaire={questionnaire}
        items={items}
        formats={formats}
        answers={answers}
        onAnswer={onAnswer}
        disabled={disabled}
        highlightIds={asked ? outstandingIds : []}
        failures={stackProps.failures}
        onRetry={stackProps.onRetry}
      />

    </div>
  )
}
